// The local read set of the INBOX (docs/design/inbox.md §6, audit P5).
//
// A READ answers with an Inbox frame, so the row of the list changes at
// once. The unread count rides the Empire frame, which comes later. In
// between, the head said "3 unread" over a list with no unread row. The
// set holds every id the reader marked read until the server has counted
// it, and the store takes those ids off the count.
//
// The socket keeps its order. The hub marks a message read, answers with
// the Inbox frame, and every Empire frame it sends after that counts the
// read. So an id leaves the set at the first Empire frame that comes
// after an Inbox frame that shows the message read (or gone). An Empire
// frame before that still counts the message as unread, and the set takes
// it off. A new hello is a new socket: a read that the old socket lost is
// not a read, so the set empties.

export class LocalReads {
  /** The ids marked read whose read no Inbox frame has shown yet. */
  private sent = new Set<number>();
  /** The ids an Inbox frame showed read; the next Empire frame counts them. */
  private shown = new Set<number>();
  /** MARK ALL READ is in flight: the count reads 0. */
  private allSent = false;
  private allShown = false;
  /** The ids MARK ALL READ marked, until the first Inbox frame after it. */
  private allIds = new Set<number>();
  /** The list `unreadIds` last scanned, and the unread ids it found. */
  private scanned: readonly { id: number; read: boolean }[] | null = null;
  private scannedIds: number[] = [];

  /** The reader marked these ids read. */
  mark(ids: Iterable<number>): void {
    for (const id of ids) {
      if (!this.shown.has(id)) this.sent.add(id);
    }
  }

  /** The reader marked every message read: the ids of the cached list
   *  read as read, and the count reads 0 until the server counted. */
  markAll(ids: Iterable<number>): void {
    for (const id of ids) {
      if (!this.shown.has(id)) this.sent.add(id);
      this.allIds.add(id);
    }
    this.allSent = true;
  }

  /** Does the reader see this message as read? */
  isRead(id: number, serverRead: boolean): boolean {
    return serverRead || this.sent.has(id) || this.shown.has(id);
  }

  /** An Inbox frame arrived: the ids it shows read (or no longer holds)
   *  wait for the next Empire frame.
   *
   *  MARK ALL READ ends at the first Inbox frame after it (review K2): the
   *  answer can be lost, so the count does not wait for a list with no
   *  unread message. A message that frame shows unread is unread again;
   *  a frame with none unread holds the count at 0 until the next Empire
   *  frame. */
  inboxFrame(msgs: readonly { id: number; read: boolean }[]): void {
    const unread = new Set<number>();
    for (const m of msgs) if (!m.read) unread.add(m.id);
    if (this.allSent) {
      this.allSent = false;
      for (const id of this.allIds) {
        if (unread.has(id)) this.sent.delete(id);
      }
      this.allIds.clear();
      this.allShown = unread.size === 0;
    }
    for (const id of this.sent) {
      if (!unread.has(id)) {
        this.sent.delete(id);
        this.shown.add(id);
      }
    }
  }

  /** An Empire frame arrived: it counts every read an earlier Inbox frame
   *  showed. */
  empireFrame(): void {
    this.shown.clear();
    this.allShown = false;
  }

  /** A new socket: a read in flight on the old one is lost. */
  reset(): void {
    this.sent.clear();
    this.shown.clear();
    this.allIds.clear();
    this.allSent = false;
    this.allShown = false;
  }

  /** The unread ids of `list`, in list order. Each Inbox frame is a new
   *  list, so one scan serves every read of the count until the next frame:
   *  the head, the cap, the dock and the tab icon read the count each
   *  render. */
  unreadIds(list: readonly { id: number; read: boolean }[]): readonly number[] {
    if (list !== this.scanned) {
      this.scanned = list;
      this.scannedIds = [];
      for (const m of list) if (!m.read) this.scannedIds.push(m.id);
    }
    return this.scannedIds;
  }

  /** The unread count of the Empire frame, less the reads it has not
   *  counted yet. `unreadIds` are the unread ids of the cached list: a
   *  read in flight counts only where the server showed the message
   *  unread, and a read an Inbox frame showed counts whole, because the
   *  cached list already shows it read. */
  unread(serverCount: number, unreadIds: Iterable<number>): number {
    if (this.allSent || this.allShown) return 0;
    let n = serverCount - this.shown.size;
    for (const id of unreadIds) {
      if (this.sent.has(id)) n--;
    }
    return Math.max(0, n);
  }
}
